import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncEffect } from 'rooks';
import { sqWorkbenchStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { ImpactReportForm, ImpactReportHeader } from '@/impactReports/ImpactReportForm.page';
import { availableStages } from '@/impactReports/impactReport.types';
import { getStageIndex } from '@/impactReports/impactReport.utilities';
import { useImpactReportCategories } from '@/impactReports/impactReports.store';
import { useImpactReportApiActions } from '@/impactReports/useImpactReportApiActions.hook';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { Modal } from '@seeqdev/qomponents';

interface ImpactReportEditModalProps {
  closeFn: (reloadTable?: boolean) => void;
  existingReport?: Record<string, any>;
}

export const ImpactReportEditModal = ({ closeFn, existingReport }: ImpactReportEditModalProps) => {
  const maxStageIndex = availableStages?.length;
  const { t } = useTranslation();
  const stateParams = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.stateParams);
  const [workbookId, setWorkbookId] = useState<string>(
    existingReport ? existingReport[SeeqNames.ImpactReport.AssignedToWorkbook] : '',
  );
  const [worksheetId, setWorksheetId] = useState<string>(
    existingReport ? existingReport[SeeqNames.ImpactReport.AssignedToWorksheet] : '',
  );
  const [stageIndex, setStageIndex] = useState(0);
  const categories = useImpactReportCategories();
  const { loadImpactReportCategories } = useImpactReportApiActions();

  useEffect(() => {
    if (existingReport) return;
    setWorkbookId(stateParams?.workbookId);
    setWorksheetId(stateParams?.worksheetId);
  }, [stateParams]);

  useAsyncEffect(async () => {
    if (!categories) {
      await loadImpactReportCategories();
    }
  }, []);

  useEffect(() => {
    if (!existingReport) return;
    const idx = getStageIndex(existingReport[SeeqNames.ImpactReport.ImpactStatus]);
    setStageIndex(idx < maxStageIndex - 1 ? idx + 1 : idx);
  }, [existingReport]);

  return categories ? (
    <Modal
      testId="impactReportEditModal"
      hideFooterButtons={true}
      open={true}
      size="4xl"
      onClose={closeFn}
      title={t('IMPACT_REPORT.CAPTURE_IMPACT')}
      customHeader={
        <ImpactReportHeader
          workbookId={workbookId}
          worksheetId={worksheetId}
          stageIndex={stageIndex}
          setStageIndex={setStageIndex}
          setWorkbookId={setWorkbookId}
          setWorksheetId={setWorksheetId}
        />
      }>
      <div className="valueCapture valueCaptureModalHeight">
        <ImpactReportForm
          categories={categories}
          report={existingReport}
          stageIndex={stageIndex}
          workbookId={workbookId}
          worksheetId={worksheetId}
          setWorkbookId={setWorkbookId}
          setWorksheetId={setWorksheetId}
          setStageIndex={setStageIndex}
          closeFn={closeFn}
        />
      </div>
    </Modal>
  ) : null;
};
